/**
 * Google Tag Manager + Google Analytics 4 bootstrap.
 *
 * - GTM is the preferred entry point: drop one container, manage everything
 *   (GA4 included) from the GTM UI.
 * - GA4 (gtag.js) is wired independently so the site can run GA without GTM
 *   when needed. If both env vars are set, both load — most teams either pick
 *   GTM-only and configure GA through it, or gtag-only.
 *
 * Both functions are no-ops when their env var is empty, so production builds
 * stay clean when analytics is intentionally disabled (e.g. preview deploys).
 */

import { env } from './env';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialised = false;

export function initAnalytics(): void {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;

  const gtmId = env.gtmId();
  const gaId = env.gaId();

  if (gtmId) injectGtm(gtmId);
  if (gaId) injectGa(gaId);
}

function injectGtm(id: string): void {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(id)}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

function injectGa(id: string): void {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer ?? [];
  // Per the GA4 install snippet, `gtag` must forward to `dataLayer.push`.
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer?.push(args);
  };
  window.gtag('js', new Date());
  // SPA: disable automatic page_view so we can fire one per route change.
  window.gtag('config', id, { send_page_view: false });
}

/** Fire a SPA page_view to GA4 and a virtual pageview event to GTM. */
export function trackPageView(path: string, title?: string): void {
  if (typeof window === 'undefined') return;

  const gaId = env.gaId();
  if (gaId && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title ?? document.title,
      page_location: window.location.href,
    });
  }

  if (env.gtmId() && window.dataLayer) {
    window.dataLayer.push({
      event: 'page_view',
      page_path: path,
      page_title: title ?? document.title,
      page_location: window.location.href,
    });
  }
}
